import React from 'react';
import { Link } from 'react-router-dom';
import { Check, ArrowRight, Star, Ticket, Gift } from 'lucide-react';
import { Reveal } from '../hooks/useReveal';
import { PRICING, CARNETS, FAQ } from '../data/mock';
import PageHero from '../components/PageHero';
import TrialCTA from '../components/TrialCTA';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';

function TrialBanner() {
  return (
    <section className="max-w-7xl mx-auto px-5 lg:px-8 -mt-10 relative z-10">
      <Reveal>
        <div className="bg-accent text-black rounded-md p-6 md:p-7 flex flex-col sm:flex-row items-center justify-between gap-5">
          <div className="flex items-center gap-4">
            <span className="w-12 h-12 grid place-items-center bg-black/10 rounded-md shrink-0"><Gift size={24} /></span>
            <div>
              <h3 className="font-display text-2xl leading-none">Séance d'essai gratuite</h3>
              <p className="font-cond tracking-wide text-sm text-black/70 mt-1">Viens tester sans engagement, c'est cadeau.</p>
            </div>
          </div>
          <Link to="/reserver" className="font-cond uppercase tracking-wider font-semibold bg-black text-accent px-6 py-3.5 rounded-sm hover:bg-[#1a1a1a] transition-colors inline-flex items-center gap-2 whitespace-nowrap">
            J'en profite <ArrowRight size={16} />
          </Link>
        </div>
      </Reveal>
    </section>
  );
}

function PricingCard({ plan }) {
  const { highlight } = plan;
  return (
    <div className={`rounded-md p-8 h-full flex flex-col relative ${highlight ? 'bg-accent text-black' : 'hx-card'}`}>
      {highlight && (
        <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-black text-gold font-cond text-xs tracking-widest px-3 py-1 rounded-full flex items-center gap-1">
          <Star size={12} fill="currentColor" /> POPULAIRE
        </span>
      )}
      <p className={`font-cond text-xs tracking-widest mb-2 ${highlight ? 'text-black/70' : 'text-accent'}`}>{plan.tag}</p>
      <h3 className="font-display text-3xl mb-4">{plan.name}</h3>
      <div className="flex items-end gap-1 mb-1">
        <span className="font-display text-6xl leading-none">{plan.price}€</span>
        <span className={`font-cond text-sm mb-2 ${highlight ? 'text-black/70' : 'text-[var(--hx-muted)]'}`}>{plan.period}</span>
      </div>
      <p className={`text-xs font-cond tracking-wide mb-6 ${highlight ? 'text-black/60' : 'text-[var(--hx-muted)]'}`}>{plan.noCommit}</p>
      <ul className="space-y-3 flex-1 mb-8">
        {plan.features.map((f) => (
          <li key={f} className="flex items-start gap-2 text-sm">
            <Check size={17} className={`shrink-0 mt-0.5 ${highlight ? 'text-black' : 'text-accent'}`} />
            <span className={highlight ? 'text-black' : 'text-[var(--hx-text)]'}>{f}</span>
          </li>
        ))}
      </ul>
      <Link
        to="/reserver"
        className={`font-cond uppercase tracking-wider font-semibold text-center py-3.5 rounded-sm transition-all inline-flex items-center justify-center gap-2 ${
          highlight ? 'bg-black text-accent hover:bg-[#1a1a1a]' : 'border border-[#333] text-white hover:border-accent hover:text-accent'
        }`}
      >
        Choisir <ArrowRight size={16} />
      </Link>
    </div>
  );
}

function CarnetCard({ carnet }) {
  return (
    <div className="hx-card group rounded-md p-8 h-full flex flex-col items-start">
      <span className="w-12 h-12 grid place-items-center bg-gold/10 border border-gold/30 rounded-md mb-5">
        <Ticket size={22} className="text-gold" />
      </span>
      <h3 className="font-display text-3xl group-hover:text-accent transition-colors">{carnet.sessions} séances</h3>
      <p className="font-cond tracking-wide text-sm text-[var(--hx-muted)] mb-5">soit {carnet.perSession}</p>
      <div className="flex items-end gap-1 mb-7 mt-auto">
        <span className="font-display text-5xl leading-none text-accent">{carnet.price}€</span>
      </div>
      <Link to="/reserver" className="w-full font-cond uppercase tracking-wider font-semibold text-center py-3 rounded-sm border border-[#333] text-white hover:border-accent hover:text-accent transition-all inline-flex items-center justify-center gap-2">
        Acheter <ArrowRight size={15} />
      </Link>
    </div>
  );
}

function FaqSection() {
  return (
    <section className="py-14 md:py-28 max-w-7xl mx-auto">
      <div className="max-w-3xl mx-auto px-5 lg:px-8">
        <Reveal className="text-center mb-12">
          <p className="hx-eyebrow mb-4">FAQ</p>
          <h2 className="font-display text-4xl md:text-5xl leading-[0.95]">Les questions fréquentes</h2>
        </Reveal>
        <Reveal>
          <Accordion type="single" collapsible className="w-full">
            {FAQ.map((f, i) => (
              <AccordionItem key={f.q} value={`item-${i}`} className="border-[#262626]">
                <AccordionTrigger className="text-left font-cond tracking-wide text-lg hover:text-accent hover:no-underline">{f.q}</AccordionTrigger>
                <AccordionContent className="text-[var(--hx-muted)] text-base leading-relaxed">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </Reveal>
      </div>
    </section>
  );
}

export default function Tarifs() {
  return (
    <>
      <PageHero
        eyebrow="Tarifs"
        title="Un abo,"
        accent="deux salles"
        img="facility1"
        subtitle="À partir de 29€ par mois pour un accès dans tes deux salles. Et ta première séance d'essai est 100% offerte."
      />

      <TrialBanner />

      {/* Abonnements */}
      <section className="py-14 md:py-24 max-w-7xl mx-auto px-5 lg:px-8">
        <Reveal className="mb-12">
          <p className="hx-eyebrow mb-4">Les abonnements</p>
          <h2 className="font-display text-4xl md:text-5xl leading-[0.95]">Choisis ton rythme</h2>
          <p className="text-[var(--hx-muted)] mt-3">Tarifs avec engagement 12 mois. <span className="text-gold font-medium">+10€/mois</span> en formule sans engagement.</p>
        </Reveal>
        <div className="grid md:grid-cols-3 gap-6 items-stretch">
          {PRICING.map((p, i) => (
            <Reveal key={p.name} delay={i * 120}>
              <PricingCard plan={p} />
            </Reveal>
          ))}
        </div>
      </section>

      {/* Carnets de séances */}
      <section className="py-14 md:py-24 bg-[#0d0d0d]">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <Reveal className="mb-12">
            <p className="hx-eyebrow mb-4">Sans abonnement</p>
            <h2 className="font-display text-4xl md:text-5xl leading-[0.95]">Carnets de séances</h2>
            <p className="text-[var(--hx-muted)] mt-3">La liberté totale : achète tes séances et viens quand tu veux.</p>
          </Reveal>
          <div className="grid sm:grid-cols-3 gap-6">
            {CARNETS.map((c, i) => (
              <Reveal key={c.sessions} delay={i * 110}>
                <CarnetCard carnet={c} />
              </Reveal>
            ))}
          </div>
          <Reveal className="text-center text-[var(--hx-muted)] mt-8 text-sm">
            Abonné CrossFit ? Ton accès Hyrox est <span className="text-accent font-medium">offert</span>.
          </Reveal>
        </div>
      </section>

      <FaqSection />

      <TrialCTA />
    </>
  );
}
